import {
  AuthProvider,
  Authenticated,
  Refine,
  AccessControlProvider,
} from '@refinedev/core'
import { RefineKbarProvider } from '@refinedev/kbar'

const apiUrl = import.meta.env.VITE_API_URL

import {
  ErrorComponent,
  ThemedLayoutV2,
  ThemedSiderV2,
  ThemedTitleV2,
  RefineThemes,
  useNotificationProvider,
  RefineSnackbarProvider,
} from '@refinedev/mui'

import { useAuth0 } from '@/utils/auth/auth0'
import { useBlockBrowserNavigation } from '@/hooks/useBlockBrowserNavigation'
import { useCallback, useRef, useMemo, useEffect, useState } from 'react'
import {
  installAuthAxios,
  setTokenGetter,
  clearTokenGetter,
  setAuthFailureHandler,
  setAuthSettled,
} from './api/authAxios'
import CssBaseline from '@mui/material/CssBaseline'
import GlobalStyles from '@mui/material/GlobalStyles'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router-v6'
import { dataProvider } from './providers/dataProvider'
import axios from 'axios'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'
import { Header } from './components/header'
import { SidebarMenu } from './components/sidebar-menu'
import { ColorModeContextProvider } from './contexts/color-mode'

import { Login } from './pages/login'

import { OrgsList } from './pages/orgs/list'
import { OrgsShow } from './pages/orgs/show'
import { FundersList } from './pages/funders/list'
import FundersShow from './pages/funders/show'
import FunderEdit from './pages/funders/edit'
import { RegionsList } from './pages/regions/list'
import { RegionsShow } from './pages/regions/show'
import { RegionCreate } from './pages/regions/create'
import { RegionEdit } from './pages/regions/edit'
import UserSpecificOpportunitiesList from './components/UserSpecificOpportunitiesList'
import UserSpecificFundersList from './components/UserSpecificFundersList'
import { OpportunitiesList } from './pages/opportunities/list'
import { OpportunitiesShow } from './pages/opportunities/show'
import OpportunityEdit from './pages/opportunities/edit'
import { UsersList } from './pages/users/list'
import { UsersShow } from './pages/users/show'
import UsersEdit from './pages/users/edit'
import { OpportunityCreate } from './pages/opportunities/create'
import { ThemeProvider } from '@mui/material/styles'
import { FunderCreate } from './pages/funders/create'
import { OrgsCreate } from './pages/orgs/create'
import { FeedbackList } from './pages/feedback/FeedbackList'
import { FeedbackDetail } from './pages/feedback/FeedbackDetail'
import TestFilters from './components/TestFilters'
import Dashboard from './components/Dashboard'
import Loading from './components/Loading'
import ScanDashboardPage from './pages/scans/dashboard'
import MatchRunComparisonPage from './pages/scans/compare'
import LlmWorkDashboard from './pages/llm-work'
import AccountsDashboardPage from './pages/accounts/dashboard'
import BillingDriftReport from './pages/billing-drift'
import { RouteGuard } from './components/RouteGuard'
import {
  MemoriesList,
  MemoriesShow,
  MemoryActivityPage,
  MemoryActivityShow,
} from './pages/memories'
import { RelationalIntroductionLetterDraftsList } from './pages/relational-introduction-letter-drafts/list'
import { RelationalIntroductionLetterDraftsShow } from './pages/relational-introduction-letter-drafts/show'
import { ProposedEditsList } from './pages/proposed-edits/list'

function BrowserNavigationBlocker() {
  const { showConfirmDialog, handleConfirmLeave, handleCancelLeave } =
    useBlockBrowserNavigation()

  return (
    <Dialog open={showConfirmDialog} onClose={handleCancelLeave}>
      <DialogTitle>Unsaved Changes</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to leave? You have unsaved changes.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelLeave}>Cancel</Button>
        <Button onClick={handleConfirmLeave} color="error">
          Leave
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function App() {
  const { isLoading, user, logout, getAccessTokenSilently } = useAuth0()

  // Bridge Auth0's token getter into the global axios auth interceptor. Registered
  // in an effect (never during render) and the whole app tree is gated on
  // `authReady`, so no descendant request can fire before the getter exists —
  // fail-closed can't brick a legitimate call, and no stale getter is published.
  const [authReady, setAuthReady] = useState(false)
  useEffect(() => {
    installAuthAxios()
    setTokenGetter(() => getAccessTokenSilently())
    setAuthFailureHandler(() => logout({ returnTo: window.location.origin }))
    setAuthReady(true)
    return () => clearTokenGetter()
  }, [getAccessTokenSilently, logout])

  // Concrete "post-auth" signal for the response interceptor: only redirect on
  // failures after initial auth has settled (exempts pre-auth / callback 401s).
  useEffect(() => {
    setAuthSettled(!isLoading && Boolean(user))
  }, [isLoading, user])

  // Permissions cache. A ref, not state: the value is never rendered, so a
  // write must not trigger a re-render, and a read must stay fresh without
  // making the cache a dependency of getPermissions (which would churn the
  // callback's identity on every fetch and cascade through authProvider /
  // accessControlProvider.can). `fetched` distinguishes "no permissions (null)"
  // from "not yet fetched" so a legitimate null is cached rather than refetched
  // on every call. Keyed by userId so the cache can never serve a previous
  // user's permissions if the session's user changes without a full reload.
  const permissionsCacheRef = useRef<{
    userId: string | null
    data: string | null
    timestamp: number
    fetched: boolean
  }>({ userId: null, data: null, timestamp: 0, fetched: false })

  const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  const fetchingRef = useRef<Promise<string | null> | null>(null)

  const getPermissions = useCallback(async () => {
    if (!user) {
      return null
    }

    const now = Date.now()
    const userId = user.sub ?? null

    // Drop the cache and any in-flight fetch if the user changed.
    if (permissionsCacheRef.current.userId !== userId) {
      permissionsCacheRef.current = {
        userId,
        data: null,
        timestamp: 0,
        fetched: false,
      }
      fetchingRef.current = null
    }

    const cache = permissionsCacheRef.current

    // Return cached permissions if still valid — including a cached null.
    if (cache.fetched && now - cache.timestamp < CACHE_DURATION) {
      return cache.data
    }

    // If already fetching, return the existing promise
    if (fetchingRef.current) {
      return fetchingRef.current
    }

    // Start new fetch
    fetchingRef.current = (async () => {
      try {
        const token = await getAccessTokenSilently()
        const response = await axios.get(
          `${apiUrl}/users/permissions/${user.sub}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        // Update cache. A failed fetch (catch below) is deliberately left
        // uncached so the next call retries rather than pinning a false null.
        permissionsCacheRef.current = {
          userId,
          data: response.data,
          timestamp: now,
          fetched: true,
        }

        return response.data
      } catch (error) {
        console.error('Error fetching permissions:', error)
        return null
      } finally {
        fetchingRef.current = null
      }
    })()

    return fetchingRef.current
  }, [user, getAccessTokenSilently])

  const authProvider: AuthProvider = useMemo(
    () => ({
      login: async () => {
        return {
          success: true,
        }
      },
      logout: async () => {
        logout({ returnTo: window.location.origin })
        return {
          success: true,
        }
      },
      onError: async (error) => {
        return { error }
      },
      check: async () => {
        try {
          const token = await getAccessTokenSilently()
          if (token) {
            // Auth header is attached centrally by the authAxios interceptor,
            // scoped to the API origin — no global axios.defaults mutation here.
            return {
              authenticated: true,
            }
          } else {
            return {
              authenticated: false,
              error: {
                message: 'Check failed',
                name: 'Token not found',
              },
              redirectTo: '/login',
              logout: true,
            }
          }
        } catch (error: any) {
          if (error.message.match(/Unauthorized email address/)) {
            logout({ returnTo: window.location.origin })
          }
          return {
            authenticated: false,
            error: new Error(error),
            redirectTo: '/login',
            logout: true,
          }
        }
      },
      getPermissions,
      getIdentity: async () => {
        if (user) {
          return {
            ...user,
            avatar: user.picture,
          }
        }
        return null
      },
    }),
    [user, getPermissions, logout]
  )

  const accessControlProvider: AccessControlProvider = {
    can: async ({ resource }) => {
      const permissions =
        authProvider.getPermissions && (await authProvider.getPermissions())

      const baseAllowedResources = [
        'dashboard',
        'funders',
        'regions',
        'opportunities',
        'feedback',
        'scans',
        'llm-work',
        'filter',
        'proposed-edits',
      ]

      // ONLY controls what shows up in the sidebar
      // if no permissions, only show base allowed
      if (!permissions) {
        return {
          can: Boolean(resource && baseAllowedResources.includes(resource)),
        }
      }

      // Full permission users see everything in sidebar
      if (permissions === 'Full') {
        return { can: true }
      }

      // Orgs Users permission - see base allowed + users and orgs
      if (permissions === 'Orgs Users') {
        const addtionalAllowedResources = ['users', 'orgs']
        const allowedResources = [
          ...baseAllowedResources,
          ...addtionalAllowedResources,
        ]
        return {
          can: Boolean(resource && allowedResources.includes(resource)),
        }
      }

      return { can: false }
    },
  }

  return isLoading || !authReady ? (
    <Loading />
  ) : (
    <BrowserRouter>
      <ThemeProvider theme={RefineThemes.Blue}>
        <RefineSnackbarProvider>
          <RefineKbarProvider>
            <ColorModeContextProvider>
              <CssBaseline />
              <GlobalStyles
                styles={{
                  html: { WebkitFontSmoothing: 'auto' },
                  // A refused location save lists each problem as its own
                  // paragraph; refine renders the toast description in an h6.
                  '.SnackbarItem-contentRoot h6': { whiteSpace: 'pre-line' },
                }}
              />
              <Refine
                dataProvider={dataProvider}
                routerProvider={routerBindings}
                authProvider={authProvider}
                accessControlProvider={accessControlProvider}
                notificationProvider={useNotificationProvider}
                resources={[
                  {
                    name: 'dashboard',
                    meta: {
                      label: 'Dashboard',
                    },
                    list: '/',
                  },
                  {
                    name: 'funding',
                    meta: {
                      label: 'Funding',
                    },
                  },
                  {
                    name: 'funders',
                    list: '/funders',
                    show: '/funders/:id',
                    edit: '/funders/edit/:id',
                    create: '/funders/create',
                    meta: {
                      canDelete: true,
                      parent: 'funding',
                    },
                  },
                  {
                    name: 'opportunities',
                    list: '/opportunities',
                    show: '/opportunities/:id',
                    edit: '/opportunities/edit/:id',
                    create: '/opportunities/create',
                    meta: {
                      parent: 'funding',
                    },
                  },
                  {
                    name: 'regions',
                    list: '/regions',
                    show: '/regions/:id',
                    edit: '/regions/edit/:id',
                    create: '/regions/create',
                    meta: {
                      canDelete: true,
                      label: 'Locations',
                      parent: 'funding',
                    },
                  },
                  {
                    name: 'filter',
                    list: '/filter',
                    meta: {
                      parent: 'funding',
                    },
                  },
                  {
                    name: 'proposed-edits',
                    list: '/proposed-edits',
                    meta: {
                      label: 'Review Queue',
                      parent: 'funding',
                    },
                  },
                  {
                    name: 'customers',
                    meta: {
                      label: 'Customers',
                    },
                  },
                  {
                    name: 'orgs',
                    list: '/orgs',
                    show: '/orgs/:id',
                    create: '/orgs/create',
                    meta: {
                      label: 'Customer Orgs',
                      canDelete: false,
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'users',
                    list: '/users',
                    show: '/users/:id',
                    edit: '/users/edit/:id',
                    meta: {
                      canDelete: false,
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'feedback',
                    list: '/feedback',
                    show: '/feedback/:id',
                    meta: {
                      label: 'Feedback',
                      canDelete: false,
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'memories',
                    list: '/memories',
                    show: '/memories/:id',
                    meta: {
                      label: 'Memories',
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'memory-activity',
                    list: '/memories/activity',
                    show: '/memories/activity/:id',
                    meta: {
                      label: 'Memory Activity',
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'relational-introduction-letter-drafts',
                    list: '/relational-introduction-letter-drafts',
                    show: '/relational-introduction-letter-drafts/:id',
                    meta: {
                      label: 'Intro Letter Drafts',
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'billing-drift',
                    list: '/billing-drift',
                    meta: {
                      label: 'Billing Drift Report',
                      parent: 'customers',
                    },
                  },
                  {
                    name: 'scans',
                    meta: {
                      label: 'Scan Dashboard',
                    },
                    list: '/scans',
                  },
                  {
                    name: 'llm-work',
                    list: '/llm-work',
                    meta: { label: 'LLM Work' },
                  },
                  {
                    name: 'Accounts',
                    meta: {
                      label: 'Accounts Dashboard',
                    },
                    list: '/accounts',
                  },
                ]}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  useNewQueryKeys: true,
                  projectId: 'AA2ohS-xwZQQC-DsUR5c',
                }}
              >
                <Routes>
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-inner"
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <ThemedLayoutV2
                          Header={Header}
                          Sider={() => (
                            <ThemedSiderV2
                              Title={({ collapsed }) => (
                                <ThemedTitleV2
                                  collapsed={collapsed}
                                  icon={false}
                                  text="Fundsorter 👍🏽 Admin"
                                />
                              )}
                              render={({ logout, collapsed }) => (
                                <>
                                  <Tooltip
                                    title="Team Processes"
                                    placement="right"
                                    disableHoverListener={!collapsed}
                                    arrow
                                  >
                                    <ListItemButton
                                      component="a"
                                      href="https://docs.google.com/document/d/1uc5BhWqVkqlYuzTH6jYHI5G25LlhTA5A5OJ9ZB4R1eA/edit?tab=t.7faremarr8h8"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        pl: 0,
                                        justifyContent: collapsed
                                          ? 'center'
                                          : 'flex-start',
                                      }}
                                    >
                                      <ListItemIcon
                                        sx={{
                                          minWidth: collapsed ? 0 : 40,
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <MenuBookIcon />
                                      </ListItemIcon>
                                      {!collapsed && (
                                        <ListItemText
                                          primary="Team Processes"
                                          primaryTypographyProps={{
                                            noWrap: true,
                                            fontSize: '14px',
                                          }}
                                        />
                                      )}
                                    </ListItemButton>
                                  </Tooltip>
                                  <SidebarMenu collapsed={collapsed} />
                                  {logout}
                                </>
                              )}
                            />
                          )}
                          Title={({ collapsed }) => (
                            <ThemedTitleV2
                              collapsed={collapsed}
                              icon={false}
                              text="Fundsorter 👍🏽 Admin"
                            />
                          )}
                        >
                          <Outlet />
                        </ThemedLayoutV2>
                      </Authenticated>
                    }
                  >
                    {/* <Route
                      index
                      element={<NavigateToResource resource="funders" />}
                    /> */}
                    <Route path="/">
                      <Route index element={<Dashboard />} />
                    </Route>

                    <Route path="/regions">
                      <Route index element={<RegionsList />} />
                      <Route path=":id" element={<RegionsShow />} />
                      <Route path="edit/:id" element={<RegionEdit />} />
                      <Route path="create" element={<RegionCreate />} />
                    </Route>

                    <Route path="/funders">
                      <Route index element={<FundersList />} />
                      <Route
                        path="assigned/:id"
                        element={<UserSpecificFundersList />}
                      />
                      <Route path=":id" element={<FundersShow />} />
                      <Route path="edit/:id" element={<FunderEdit />} />
                      <Route path="create" element={<FunderCreate />} />
                    </Route>
                    <Route path="/opportunities">
                      <Route index element={<OpportunitiesList />} />
                      <Route
                        path="assigned/:id"
                        element={<UserSpecificOpportunitiesList />}
                      />
                      <Route path=":id" element={<OpportunitiesShow />} />
                      <Route path="edit/:id" element={<OpportunityEdit />} />
                      <Route path="create" element={<OpportunityCreate />} />
                    </Route>
                    {/* START: Full and Orgs Users acccess only */}
                    <Route path="/orgs">
                      <Route
                        index
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsList />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="create"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsCreate />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/details"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="details" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/billing"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="billing" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/activity"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="activity" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/billing-history"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="billing-history" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/funding"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="funding" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/memories"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="memories" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id/onboarding"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow tab="onboarding" />
                          </RouteGuard>
                        }
                      />

                      <Route
                        path=":id"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <OrgsShow />
                          </RouteGuard>
                        }
                      />
                    </Route>
                    <Route path="users">
                      <Route
                        index
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <UsersList />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <UsersShow />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="edit/:id"
                        element={
                          <RouteGuard
                            allowedPermissions={['Full', 'Orgs Users']}
                          >
                            <UsersEdit />
                          </RouteGuard>
                        }
                      />
                    </Route>
                    {/* END: Full and Orgs Users acccess only */}

                    <Route path="/feedback">
                      <Route index element={<FeedbackList />} />
                      <Route path=":id" element={<FeedbackDetail />} />
                    </Route>
                    <Route path="/filter">
                      <Route index element={<TestFilters />} />
                      <Route path="list" element={<TestFilters />} />
                    </Route>
                    <Route path="/scans">
                      <Route index element={<ScanDashboardPage />} />
                      <Route
                        path="compare"
                        element={<MatchRunComparisonPage />}
                      />
                    </Route>

                    <Route path="/llm-work" element={<LlmWorkDashboard />} />
                    <Route
                      path="/billing-drift"
                      element={
                        <RouteGuard allowedPermissions={['Full']}>
                          <BillingDriftReport />
                        </RouteGuard>
                      }
                    />
                    <Route path="/accounts">
                      <Route
                        index
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage />{' '}
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="financial"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage tab="financial" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="xero"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage tab="xero" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="reconciliation"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage tab="reconciliation" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="discounts"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage tab="discounts" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="onboarding"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage tab="onboarding" />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="activity"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <AccountsDashboardPage tab="activity" />
                          </RouteGuard>
                        }
                      />
                    </Route>
                    <Route path="/memories">
                      <Route
                        index
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <MemoriesList />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="activity"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <MemoryActivityPage />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="activity/:id"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <MemoryActivityShow />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <MemoriesShow />
                          </RouteGuard>
                        }
                      />
                    </Route>
                    <Route path="/proposed-edits">
                      <Route index element={<ProposedEditsList />} />
                    </Route>
                    <Route path="/relational-introduction-letter-drafts">
                      <Route
                        index
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <RelationalIntroductionLetterDraftsList />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <RouteGuard allowedPermissions={['Full']}>
                            <RelationalIntroductionLetterDraftsShow />
                          </RouteGuard>
                        }
                      />
                    </Route>
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-outer"
                        fallback={<Outlet />}
                      >
                        <NavigateToResource />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<Login />} />
                  </Route>
                </Routes>

                <UnsavedChangesNotifier />
                <BrowserNavigationBlocker />
                <DocumentTitleHandler />
              </Refine>
            </ColorModeContextProvider>
          </RefineKbarProvider>
        </RefineSnackbarProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
